import { useMemo } from "react";
import { AlertCircle, ChevronRight, Edit3, Trash2 } from "lucide-react";

import { useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";

const STATUS_TONES = {
  published: "bg-primary/10 text-primary border-primary/30",
  draft: "bg-muted text-muted-foreground border-border",
  archived: "bg-muted text-muted-foreground border-border",
};

const SurveyListItem = ({
  survey,
  userId,
  labels,
  options,
  caps,
  handlers,
}) => {
  const { t } = useTran();
  const { actionLabel, secondaryActionLabel } = labels;
  const {
    showQuestionsByDefault,
    showCategoryBadge,
    showResponseCount,
    showQuestionCountBubble,
    hideMetadata,
    showPrimaryAction,
    showShareButton,
  } = options;
  const {
    canEditStandard,
    canPublishStandard,
    canDeleteStandard,
    canEditCustom,
    canPublishCustom,
  } = caps;
  const {
    onSelect,
    onSecondaryAction,
    onShare,
    onEditStandard,
    onEditCustom,
    onPublishStandard,
    onPublishCustom,
    onArchiveStandard,
    onArchiveCustom,
    onDeleteStandard,
    onDeleteCustom,
  } = handlers;

  const status = (survey.status || "draft").toLowerCase();
  const statusTone = STATUS_TONES[status] || STATUS_TONES.draft;
  const isPublished = status === "published";
  const isStandard = Boolean(survey.systemManaged);
  const questionCount =
    survey.questionCount ??
    (Array.isArray(survey.questions) ? survey.questions.length : 0);
  const responseCount = survey.responseCount ?? 0;
  const expiresAtDate = survey.expiresAt ? new Date(survey.expiresAt) : null;
  const now = new Date();
  const diffInDays = expiresAtDate
    ? (expiresAtDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    : null;
  const isExpiringSoon =
    diffInDays !== null && diffInDays > 0 && diffInDays <= 3;
  const isExpired = expiresAtDate ? now > expiresAtDate : false;
  const isOwner = survey.createdBy ? survey.createdBy === userId : true;
  const canPublish = isStandard ? canPublishStandard : canPublishCustom;
  const handlePublish = isStandard ? onPublishStandard : onPublishCustom;
  const handleArchive = isStandard ? onArchiveStandard : onArchiveCustom;
  const handleDelete = isStandard ? onDeleteStandard : onDeleteCustom;
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];
  const groupedQuestions = useMemo(() => {
    if (!showQuestionsByDefault) return {};
    const grouped = {};
    (survey.questions || []).forEach((question) => {
      const key = question.category || "common";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(question);
    });
    return grouped;
  }, [survey.questions, showQuestionsByDefault]);

  return (
    <Card
      className={cn(
        "group relative border border-border/70 p-5 shadow-sm transition hover:-translate-y-[1px] hover:shadow-md",
        isExpired ? "bg-card/70" : "bg-card/80",
      )}
    >
      {isExpired ? (
        <div className="pointer-events-none absolute -top-3 left-1/2 z-10 -translate-x-1/2 scale-95 opacity-0 transition-all group-hover:scale-100 group-hover:opacity-100">
          <div className="flex items-center gap-2 rounded-full bg-foreground px-4 py-1.5 text-xs font-medium text-background shadow-lg">
            <AlertCircle className="h-3.5 w-3.5 text-primary-foreground" />
            {t("This survey expired on")} {expiresAtDate?.toLocaleDateString()}
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-4">
          {showQuestionCountBubble ? (
            <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-radius bg-primary text-sm font-semibold text-primary-foreground shadow-sm lg:flex">
              {questionCount || "—"}
            </div>
          ) : null}
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <p className="truncate text-lg font-semibold text-foreground">
                {survey.title || "—"}
              </p>
              {survey.category && showCategoryBadge ? (
                <Badge variant="secondary">{survey.category}</Badge>
              ) : null}
            </div>
            {survey.description ? (
              <p className="line-clamp-2 text-sm text-muted-foreground">
                {survey.description}
              </p>
            ) : null}
            {!hideMetadata ? (
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <Badge className={cn("border", statusTone)}>
                  {status.toUpperCase()}
                </Badge>
                <span className="rounded-full bg-muted/60 px-3 py-1 font-medium">
                  {questionCount}{" "}
                  {questionCount === 1 ? t("question") : t("questions")}
                </span>
                {showResponseCount ? (
                  <span className="rounded-full bg-muted/40 px-3 py-1 font-medium">
                    {responseCount} {t("responses")}
                  </span>
                ) : null}
                <span className="rounded-full bg-muted/30 px-3 py-1 font-medium">
                  {t("Updated")}{" "}
                  {new Date(
                    survey.updatedAt || survey.createdAt || Date.now(),
                  ).toLocaleString()}
                </span>
                {survey.expiresAt ? (
                  <span
                    className={cn(
                      "rounded-full bg-muted/30 px-3 py-1 font-medium",
                      isExpired && "line-through",
                    )}
                  >
                    {t("Expires")} {expiresAtDate?.toLocaleDateString()}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {canEditStandard && isStandard && !isPublished && onEditStandard ? (
            <Can
              resource="surveys"
              action="update"
              userPermissions={permissions}
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEditStandard(survey)}
                disabled={isExpired}
              >
                {t("Edit")}
              </Button>
            </Can>
          ) : null}

          {showPrimaryAction && actionLabel && isPublished ? (
            <Can
              resource="surveys"
              action="create"
              userPermissions={permissions}
            >
              <Button
                size="sm"
                onClick={() => onSelect?.(survey)}
                disabled={isExpired}
              >
                {actionLabel}
              </Button>
            </Can>
          ) : null}

          {secondaryActionLabel && onSecondaryAction ? (
            <Can resource="surveys" action="view" userPermissions={permissions}>
              <Button
                size="sm"
                variant="outline"
                className="flex items-center gap-1"
                onClick={() => onSecondaryAction(survey)}
              >
                {secondaryActionLabel}
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </Can>
          ) : null}

          {showShareButton && isPublished ? (
            <Can resource="surveys" action="view" userPermissions={permissions}>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onShare?.(survey)}
                disabled={isExpired}
              >
                {t("Share")}
              </Button>
            </Can>
          ) : null}

          {canPublish && handlePublish && !isPublished ? (
            <Can
              resource="surveys"
              action="update"
              userPermissions={permissions}
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePublish(survey)}
                disabled={isExpired || (!isStandard && !isOwner)}
              >
                {t("Publish")}
              </Button>
            </Can>
          ) : null}

          {/* {canPublish && handleArchive && isPublished ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleArchive(survey)}
            >
              {t("Unpublish")}
            </Button>
          ) : null} */}

          {canEditCustom && !isStandard && !isPublished ? (
            <Can
              resource="surveys"
              action="update"
              userPermissions={permissions}
            >
              <Button
                size="sm"
                variant="outline"
                onClick={() => onEditCustom?.(survey)}
                disabled={isExpired}
              >
                <Edit3 className="mr-1 h-3.5 w-3.5" />
                {t("Edit")}
              </Button>
            </Can>
          ) : null}

          {handleDelete &&
          ((isStandard && canDeleteStandard) ||
            (!isStandard && canEditCustom)) ? (
            <Can
              resource="surveys"
              action="delete"
              userPermissions={permissions}
            >
              <Button
                size="sm"
                variant="destructive"
                onClick={() => handleDelete(survey)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {t("Delete")}
              </Button>
            </Can>
          ) : null}
        </div>
      </div>

      {isExpiringSoon ? (
        <div className="mt-4 flex items-center gap-2 rounded-radius bg-primary/10 p-3 text-sm font-medium text-primary">
          <AlertCircle className="h-4 w-4" />
          {t("This survey will expire in")} {Math.ceil(diffInDays || 0)}{" "}
          {Math.ceil(diffInDays || 0) === 1 ? t("Day") : t("Days")}
        </div>
      ) : null}

      {showQuestionsByDefault && Object.keys(groupedQuestions).length ? (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {Object.keys(groupedQuestions).map((category) => (
            <div
              key={category}
              className="rounded-radius border border-border bg-muted/30 p-3"
            >
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {category}
              </p>
              <ul className="space-y-1.5">
                {groupedQuestions[category].map((question, index) => (
                  <li
                    key={question._id || `${survey._id}-${index}`}
                    className="flex text-sm text-muted-foreground"
                  >
                    <span className="mr-2 w-5 flex-shrink-0 text-right text-xs text-muted-foreground">
                      {index + 1}.
                    </span>
                    <div className="flex-1">{question.questionText}</div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
};

export default SurveyListItem;
